import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DossiersJaunesService } from '@lib/dossiers-jaunes/dossiers-jaunes.service';
import { ConsultantDocument } from '@lib/models/consultant/consultant-document.model';
import { DossierJaune } from '@lib/models/dossier-jaune/dossier-jaune';
import { OuiButton } from 'omnium-ui/button';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiLoading } from 'omnium-ui/loading';
import { OuiSnackbarService } from 'omnium-ui/snackbar';

@Component({
  selector: 'lib-fiche-consultant-archive',
  standalone: true,
  imports: [OuiButton, OuiIconModule, OuiLoading],
  templateUrl: './fiche-consultant-archive.component.html',
  styleUrl: './fiche-consultant-archive.component.scss',
})
export class FicheConsultantArchiveComponent implements OnInit {
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly dossiersJaunesService = inject(DossiersJaunesService);

  readonly codeConsultant = input.required<string | undefined>();
  readonly searchValue = input.required<string>();
  readonly archiveRole = input.required<number>();

  readonly documentOpen = output<{
    document: ConsultantDocument;
    isUploadDoc: boolean;
  }>();
  readonly resetSearchControl = output();

  readonly isLoading = signal<boolean>(true);
  readonly isTopFolder = signal<boolean>(true);
  readonly currentPath = computed(() =>
    this.dossiersJaunes().length > 0
      ? this.dossiersJaunes()[this.dossiersJaunes().length - 1].path
      : ''
  );
  readonly favPath = signal<string>('');
  readonly isFavPath = computed(() => {
    return this.favPath() !== '' &&
      this.dossiersJaunes().find(dj => dj.path === this.favPath())
      ? 'fav'
      : 'not-fav';
  });
  readonly dossiersJaunes = signal<DossierJaune[]>([]);
  readonly dossiersJaunesFiltered = computed(() =>
    this.dossiersJaunes().filter(
      dj =>
        dj.name === '..' ||
        this.searchValue() === '' ||
        dj.name.toLocaleLowerCase().includes(this.searchValue())
    )
  );

  readonly goBackFolder: DossierJaune = { isFolder: true, name: '..', path: '\\' };

  ngOnInit() {
    if (this.codeConsultant()) {
      this.navigateIntoFolder('', this.codeConsultant(), this.archiveRole());
    }
  }

  onFolderOrDocClick(documentJaune: DossierJaune) {
    if (documentJaune.isFolder) {
      this.isLoading.set(true);
      this.resetSearchControl.emit();
      if (documentJaune.name === '..') {
        this.navigateIntoFolder(documentJaune.path);
      } else {
        this.navigateIntoFolder(documentJaune.path + documentJaune.name);
      }
    } else {
      this.openDocument(documentJaune.path, documentJaune.name);
    }
  }

  navigateIntoFolder(
    newFolder: string,
    codeConsultant: string | undefined | null = null,
    archiveRole: number | null = null
  ) {
    this.dossiersJaunesService
      .getDossiersJaunesContent(newFolder, codeConsultant, archiveRole)
      .then(doc => {
        if (doc.find(d => d.path !== '')) {
          this.isTopFolder.set(false);
        } else {
          this.isTopFolder.set(true);
        }

        if (codeConsultant !== null) {
          this.favPath.set(doc[0].path);
        }

        if (!this.isTopFolder()) {
          const splitPath = doc[0].path.endsWith('/')
            ? doc[0].path.slice(0, -1).split('/')
            : doc[0].path.split('/');
          this.goBackFolder.path = splitPath.slice(0, -1).join('/');
          this.dossiersJaunes.set([this.goBackFolder, ...doc]);
        } else {
          this.dossiersJaunes.set(doc);
        }
      })
      .finally(() => this.isLoading.set(false));
  }

  openDocument(path: string, name: string) {
    this.dossiersJaunesService.getDossierJauneFileUrl(path + name).then(fileUrl => {
      this.documentOpen.emit({
        document: {
          url: fileUrl,
          documentName: 'Archive',
          date: new Date(),
          fileName: name,
          id: 'egreg',
          typeDocument: 'photo_identite',
        },
        isUploadDoc: false,
      });
    });
  }

  async setShortcutPath() {
    if (this.codeConsultant() && this.favPath() !== this.currentPath()) {
      try {
        const newFavPath = await this.dossiersJaunesService.setDossierJauneShortcutPath(
          this.codeConsultant()!,
          this.currentPath()
        );

        this.favPath.set(newFavPath);

        this.snackbarService.open(
          'Votre raccourci a bien été enregistré',
          'success',
          5000,
          { horizontal: 'left', vertical: 'bottom' }
        );
      } catch (error) {
        console.error(error);

        this.snackbarService.open(
          "Une erreur est survenue lors de l'enregistrement du raccourci",
          'error',
          5000,
          { horizontal: 'left', vertical: 'bottom' }
        );
      }
    }
  }
}
